import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { apiErrorMessage } from '../../lib/errors'
import type { TableColumn } from '../../lib/tableView'
import { useTableView } from '../../lib/useTableView'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { useAuth } from '../auth/context'
import { listAlertWindows, updateAlertWindows, type AlertWindow } from './api'

/**
 * What each column of the windows sorts and filters by (TRKC-110). The windows' own column
 * sorts by what is saved, never by a box being typed in: a row must not move under the
 * keyboard. A paper that blocks sorts before one that only warns.
 */
function windowColumns(t: TFunction): TableColumn<AlertWindow>[] {
  const typeName = (row: AlertWindow) => t(`documents.types.${row.documentType}`)

  return [
    { key: 'documentType', sortValue: typeName, filter: { kind: 'text', text: typeName } },
    { key: 'warningDays', sortValue: (row) => (row.warningDays.length === 0 ? null : Math.max(...row.warningDays)) },
    { key: 'demands', sortValue: (row) => (row.requiredOwners.length > 0 ? 0 : row.critical ? 1 : 2) },
  ]
}

/** The heading each column's filter box is named after. */
const WINDOW_HEADINGS: Record<string, string> = {
  documentType: 'alertWindows.documentType',
  warningDays: 'alertWindows.warningDays',
  demands: 'alertWindows.demands',
}

/** No windows yet: one array, so the view is not recomputed for an empty one every render. */
const NO_WINDOWS: AlertWindow[] = []

/**
 * How far ahead each kind of paper starts asking for attention.
 *
 * Admin-only, because it changes what everyone sees (Hugo, 2026-08-28). Emptying a
 * type's windows is the one way to quiet a paper that shouts too often — and it
 * silences the WARNINGS only. An expired document still blocks whoever holds it,
 * always, which is the sentence this screen must never let anybody miss.
 */

/**
 * What went wrong, and which sentence to fall back on. Kept apart because this screen
 * has two failures with opposite meanings: a load that failed changed nothing, and
 * telling an admin "could not save these windows" when he has not typed anything is a
 * lie about his own data.
 */
type Failure =
  | { kind: 'request'; error: unknown; fallbackKey: string }
  | { kind: 'local'; messageKey: string }

export function AlertWindowsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [windows, setWindows] = useState<AlertWindow[] | null>(null)
  const columns = useMemo(() => windowColumns(t), [t])
  const view = useTableView(windows ?? NO_WINDOWS, columns)
  const [isLoading, setIsLoading] = useState(true)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [savingType, setSavingType] = useState<string | null>(null)
  const [savedType, setSavedType] = useState<string | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  // Records the last save could not re-judge: they were being edited mid-run and kept
  // their alerts under the OLD windows. Never folded into "Saved" — a save that quietly
  // moved nine tenths of the alerts looks exactly like one that moved them all.
  const [skipped, setSkipped] = useState(0)

  const isAdmin = user?.roles.includes('Admin') ?? false

  useEffect(() => {
    if (!isAdmin) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    void (async () => {
      try {
        const loaded = await listAlertWindows()

        if (!cancelled) {
          setWindows(loaded)
          setDrafts(Object.fromEntries(loaded.map((w) => [w.documentType, format(w.warningDays)])))
        }
      } catch (caught) {
        if (!cancelled) {
          setFailure({ kind: 'request', error: caught, fallbackKey: 'alertWindows.loadError' })
        }
      } finally {
        // Separate from `windows === null`: a load that failed is not a load still
        // running, and conflating them leaves "Loading…" spinning under the error
        // banner for as long as the tab stays open.
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isAdmin])

  async function handleSave(documentType: string) {
    const days = parseDays(drafts[documentType] ?? '')

    // Refused here rather than sent, because there is nothing to send: a token that is
    // not a number cannot travel as an int, and dropping it would turn "abc" into an
    // empty list — which is how this screen SILENCES a document type. The sentence is
    // the server's own, so there is no second vocabulary for one rule.
    if (days === null) {
      setSavedType(null)
      setFailure({ kind: 'local', messageKey: 'errors.alert_windows_invalid' })
      return
    }

    setSavingType(documentType)
    setSavedType(null)
    setFailure(null)
    setSkipped(0)

    try {
      const saved = await updateAlertWindows(documentType, days)

      // Redrawn from the server's answer, not from what was typed: it is the server
      // that sorts and de-duplicates them, and the box must show what is now true.
      setWindows((current) =>
        (current ?? []).map((w) => (w.documentType === documentType ? saved.window : w)),
      )
      setDrafts((current) => ({ ...current, [documentType]: format(saved.window.warningDays) }))
      setSavedType(documentType)
      setSkipped(saved.evaluation.conflicts)
    } catch (caught) {
      setFailure({ kind: 'request', error: caught, fallbackKey: 'alertWindows.saveError' })
    } finally {
      setSavingType(null)
    }
  }

  const trail = [{ label: t('nav.alerts'), to: '/alerts' }, { label: t('alerts.settings') }]

  if (!isAdmin) {
    return (
      <section className="page">
        <Breadcrumbs trail={trail} />
        <header className="page__header">
          <h1>{t('alertWindows.title')}</h1>
        </header>
        <p className="placeholder">{t('alertWindows.adminOnly')}</p>
      </section>
    )
  }

  return (
    <section className="page">
      <Breadcrumbs trail={trail} />
      <header className="page__header">
        <h1>{t('alertWindows.title')}</h1>
        <p className="page__subtitle">{t('alertWindows.subtitle')}</p>
      </header>

      <p className="alert alert--warning" role="note">
        {t('alertWindows.expiryNeverSilenced')}
      </p>

      {failure !== null && (
        <p className="alert" role="alert">
          {failure.kind === 'local'
            ? t(failure.messageKey)
            : apiErrorMessage(failure.error, t, failure.fallbackKey)}
        </p>
      )}

      {skipped > 0 && (
        <p className="alert alert--warning" role="status">
          {t('alertWindows.evaluationConflicts', { count: skipped })}
        </p>
      )}

      {isLoading ? (
        <p className="placeholder">{t('alertWindows.loading')}</p>
      ) : windows === null ? null : (
        <TableScroll label={t('alertWindows.title')}>
          <table className="table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <SortableHeader key={column.key} view={view} column={column.key}>
                    {t(WINDOW_HEADINGS[column.key] ?? column.key)}
                  </SortableHeader>
                ))}
                <th scope="col">
                  <span className="visually-hidden">{t('alertWindows.save')}</span>
                </th>
              </tr>
              <tr className="table__filters">
                {columns.map((column) => (
                  <FilterCell key={column.key} view={view} column={column} label={t(WINDOW_HEADINGS[column.key] ?? column.key)} />
                ))}
                <td />
              </tr>
            </thead>
            <tbody>
              {view.rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="placeholder">
                    {t('table.noneMatch')}
                  </td>
                </tr>
              )}
              {view.rows.map((row) => (
                <tr key={row.documentType}>
                  <td>{t(`documents.types.${row.documentType}`)}</td>
                  <td>
                    <input
                      type="text"
                      className="alert-windows__input"
                      inputMode="numeric"
                      aria-label={t('alertWindows.warningDaysFor', {
                        type: t(`documents.types.${row.documentType}`),
                      })}
                      value={drafts[row.documentType] ?? ''}
                      placeholder={t('alertWindows.none')}
                      // Waits with the buttons: the answer redraws this box, so a draft
                      // typed while it is in flight would be overwritten by it.
                      disabled={savingType !== null}
                      onChange={(event) => {
                        const typed = event.target.value
                        setDrafts((current) => ({ ...current, [row.documentType]: typed }))
                        // "Saved" is a statement about what the server holds. The moment
                        // the box stops matching it, the label has to stop claiming it.
                        setSavedType((current) => (current === row.documentType ? null : current))
                      }}
                    />
                  </td>
                  <td>
                    <Demands row={row} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="button button--quiet"
                      onClick={() => void handleSave(row.documentType)}
                      // Every row waits while one saves: a save re-judges the whole
                      // tenant, and two runs at once would answer out of order — the
                      // last answer owning "Saved" and the skipped count for a row it
                      // never touched.
                      disabled={savingType !== null}
                    >
                      {savingType === row.documentType
                        ? t('alertWindows.saving')
                        : savedType === row.documentType
                          ? t('alertWindows.saved')
                          : t('alertWindows.save')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {!isLoading && windows !== null && <FilterSummary view={view} />}
    </section>
  )
}

/**
 * What this paper actually demands, so the consequence of quieting it is on the same
 * row as the box that quiets it.
 */
function Demands({ row }: { row: AlertWindow }) {
  const { t } = useTranslation()

  if (row.requiredOwners.length > 0) {
    return (
      <span>
        {t('alertWindows.requiredFor', {
          owners: row.requiredOwners.map((o) => t(`alerts.ownerKind.${o}`)).join(', '),
        })}
      </span>
    )
  }

  return (
    <span className="muted">
      {row.critical ? t('alertWindows.criticalOnly') : t('alertWindows.informational')}
    </span>
  )
}

const format = (days: number[]): string => days.join(', ')

/**
 * Whatever somebody typed, as whole days — or null when it cannot be sent at all.
 *
 * **Whole numbers, and that is not fussiness.** The endpoint binds
 * `IReadOnlyList<int>`, so a fraction fails inside System.Text.Json BEFORE the handler
 * runs: the `catch (DomainException)` carrying `alert_windows_invalid` never fires and
 * the framework answers a bare 400 with an empty body (a 500 in Development), which the
 * portal can only render as its generic "could not save". So `7.5` travels as `7`'s
 * floor — `0.5` becomes `0`, and the server refuses it with the code that says
 * "between 1 and 365", which is the sentence the admin needed.
 *
 * **All or nothing**, because an empty list is not a neutral outcome here: it is how a
 * document type gets silenced. Dropping the tokens that make no sense would turn a
 * typo into a deliberate-looking silence.
 *
 * An empty BOX still means empty windows — that is the deliberate way to quiet a type.
 */
function parseDays(input: string): number[] | null {
  const tokens = input.split(/[\s,;]+/).filter((part) => part.length > 0)
  const days = tokens.map((part) => Math.floor(Number(part)))

  return days.every((value) => Number.isSafeInteger(value)) ? days : null
}
